import { ReactElement, useCallback, useEffect, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { ContentLabelField } from '@hopskipdrive/react-components';
import { useQueryClient } from '@tanstack/react-query';
import debounce from 'lodash/debounce';
import * as Yup from 'yup';

import { User } from '../../../../auth/types/user';
import AssigneeSelect from '../../../components/AssigneeSelect';
import { useGetAllAssignees } from '../../../hooks/useIncidentApi';
import { IncidentIndividual } from '../../../types/incidents';

interface AssigneeIdFieldProps {
  defaultValue?: IncidentIndividual;
}

export const assigneeIdValidation = Yup.number().required('Assignee selection is required').nullable();

export const AssigneeIdField = ({ defaultValue }: AssigneeIdFieldProps): ReactElement => {
  const recordsPerPage = 10;
  const [lastRequest, setLastRequest] = useState('all');
  const [query, setQuery] = useState({
    page: 1,
    perPage: recordsPerPage,
    q: '',
    roles: ['trust_safety'],
  });
  const [options, setOptions] = useState<IncidentIndividual[]>([]);
  const [currentUser, setCurrentUser] = useState<IncidentIndividual>();

  const queryClient = useQueryClient();
  const authResponse = queryClient.getQueryState<User>(['getAuth']);
  const { isFetching, data: response } = useGetAllAssignees(query);
  const { control } = useFormContext();
  const { field, fieldState } = useController({
    name: 'assigneeId',
    control,
    defaultValue: defaultValue?.id,
  });

  const getNextPage = () => {
    setQuery({
      page: query.page + 1,
      perPage: recordsPerPage,
      q: query.q,
      roles: ['trust_safety'],
    });
    setLastRequest('all');
  };

  const debouncedSearch = useCallback(
    debounce((input: string) => {
      setQuery({ page: 1, perPage: recordsPerPage, q: input, roles: ['trust_safety'] });
      setLastRequest('search');
    }, 1000),
    []
  );

  const search = (input: string) => {
    debouncedSearch(input);
  };

  useEffect(() => {
    if (response?.data) {
      if (lastRequest === 'search') {
        setOptions(response.data);
      } else {
        setOptions([...options, ...response.data]);
      }
    }
  }, [response]);

  useEffect(() => {
    if (authResponse?.data) {
      setCurrentUser({
        id: authResponse.data.id,
        firstName: authResponse.data.firstName,
        lastName: authResponse.data.lastName.split(' ')[1],
        slug: authResponse.data.slug,
      });

      if (!field.value && !defaultValue) {
        field.onChange(authResponse.data.id);
      }
    }
  }, [authResponse]);

  return (
    <ContentLabelField
      id="assigneeId"
      title="Assigned to"
      editMode={true}
      required={true}
      editView={
        <AssigneeSelect
          page={query.page}
          perPage={query.perPage}
          isLoading={isFetching}
          loadNextPage={getNextPage}
          search={search}
          options={options}
          value={[...options, defaultValue, currentUser].find((item) => item?.id === field.value) || null}
          onChange={(selected) => {
            field.onChange(selected?.id || null);
          }}
          error={!!fieldState.error}
          helperText={fieldState.error?.message}
          placeholder="Select"
        />
      }
    />
  );
};
